import React, { useEffect, useState } from "react";
import { Modal, Alert, Button, ConfigProvider, Steps } from "antd";
import { server } from "../../../../api";
import { resolveImageUrl } from "../utils/imageUtils";
import { EDIT_COLOR } from "../utils/constants";
import { hasWebGL2 } from "../../LiveDashboardModal/components/GeoBackdrop";
import { useGeoAlign, appliedFrame, STEP } from "./useGeoAlign";
import GeoAlignMap from "./GeoAlignMap";
import {
  EMPTY_HINT,
  FindStep,
  AlignStep,
  Readouts,
  SizeAlert,
  DisplayOptions,
} from "./GeoAlignPanels";

/**
 * 對齊地圖: a near-full-screen view over the plan editor. A north-up
 * MapLibre map shows the site at true size (the plan letterboxed exactly like
 * the canvas, its outline, its stations and 防護區), in two steps:
 * ① 找到位置 (paste or click the site centre; placing moves on to ②) and
 * ② 對齊 (drag, rotate, scale, keys, readouts). 套用 hands
 * {lat, lng, rotationDeg} of the 0,0 corner plus {width, height} back to the
 * form; nothing is saved here. `savedDimensions` is the size 保存 last wrote.
 */

const VIEW_HEIGHT = "calc(90vh - 124px)";
const SIDEBAR_PX = 300;

const useImageSize = (url) => {
  const [size, setSize] = useState(null);
  useEffect(() => {
    setSize(null);
    if (!url) return undefined;
    const img = new window.Image();
    img.onload = () =>
      setSize({ width: img.naturalWidth, height: img.naturalHeight });
    img.src = url;
    return () => {
      img.onload = null;
    };
  }, [url]);
  return size;
};

const disabledReason = ({ supported, hasLayers, ref }) => {
  if (!supported) return "此瀏覽器不支援 WebGL2，無法顯示互動地圖";
  if (!hasLayers) return "此場域沒有可用的地圖圖層";
  if (!ref) return "尚未放置場域，無法套用";
  return null;
};

const emptyBanner = {
  position: "absolute",
  top: 12,
  left: 12,
  right: 12,
  width: "fit-content",
  margin: "0 auto",
  padding: "8px 14px",
  borderRadius: 6,
  background: "rgba(255, 255, 255, 0.94)",
  boxShadow: "0 1px 4px rgba(0, 0, 0, 0.2)",
  fontSize: 14,
  pointerEvents: "none",
};

const Footer = ({ reason, onCancel, onApply }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 8,
    }}
  >
    {reason && (
      <span
        data-testid="geo-apply-reason"
        style={{ marginRight: "auto", color: "#595959" }}
      >
        {reason}
      </span>
    )}
    <Button onClick={onCancel}>取消</Button>
    <Button type="primary" disabled={!!reason} onClick={onApply}>
      套用
    </Button>
  </div>
);

const GeoAlignView = ({
  open,
  geo,
  dimensions,
  imageUrl,
  tileLayers,
  stations = [],
  onCancel,
  onApply,
  savedDimensions,
}) => {
  const url = resolveImageUrl(imageUrl, server);
  const image = useImageSize(open ? url : null);
  const supported = hasWebGL2();
  const hasLayers = tileLayers.length > 0;
  const canMap = supported && hasLayers;
  const align = useGeoAlign({ open, geo, dimensions });
  const { ref, step, floor } = align;
  const [opacity, setOpacity] = useState(0.6);
  const [layerId, setLayerId] = useState(tileLayers[0]?.id);
  const reason = disabledReason({ supported, hasLayers, ref });

  const stepItems = ["找到位置", "對齊"].map((title, i) => ({
    title,
    disabled: i !== STEP.FIND && !ref,
  }));

  return (
    <ConfigProvider theme={{ token: { colorPrimary: EDIT_COLOR } }}>
      <Modal
        title="對齊地圖"
        open={open}
        width="90vw"
        style={{ top: "5vh", paddingBottom: 0, maxWidth: 1800 }}
        styles={{ body: { height: VIEW_HEIGHT } }}
        destroyOnClose
        onCancel={onCancel}
        footer={
          <Footer
            reason={reason}
            onCancel={onCancel}
            onApply={() => onApply(appliedFrame(ref, floor))}
          />
        }
      >
        {!canMap && <Alert type="error" showIcon message={reason} />}
        {canMap && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              height: "100%",
            }}
          >
            <Steps
              size="small"
              current={step}
              onChange={align.setStep}
              items={stepItems}
              style={{ maxWidth: 720 }}
            />
            <div style={{ display: "flex", gap: 12, flex: 1, minHeight: 0 }}>
              <aside
                style={{
                  flex: `0 0 ${SIDEBAR_PX}px`,
                  display: "flex",
                  flexDirection: "column",
                  gap: 20,
                  overflowY: "auto",
                  paddingRight: 4,
                }}
              >
                {step === STEP.FIND && <FindStep align={align} />}
                {step === STEP.ALIGN && <AlignStep />}
                <DisplayOptions
                  opacity={opacity}
                  onOpacity={setOpacity}
                  tileLayers={tileLayers}
                  layerId={layerId}
                  onLayer={setLayerId}
                />
                {ref && <Readouts siteRef={ref} floor={floor} align={align} />}
                <SizeAlert saved={savedDimensions} floor={floor} />
              </aside>
              <div style={{ flex: 1, minWidth: 0, position: "relative" }}>
                <GeoAlignMap
                  tileLayers={tileLayers}
                  layerId={layerId}
                  siteRef={ref}
                  floor={floor}
                  image={image}
                  imageUrl={url}
                  opacity={opacity}
                  mode={step === STEP.FIND ? "place" : "adjust"}
                  stations={stations}
                  focus={align.focus}
                  onRef={align.setRef}
                  onFrame={align.setFrame}
                  onMapClick={align.clickMap}
                />
                {!ref && <div style={emptyBanner}>{EMPTY_HINT}</div>}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </ConfigProvider>
  );
};

export default GeoAlignView;
